import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, Trash2, ChevronDown, Mail, Phone,
  Building2, MessageSquare, Calendar, FileText, Inbox,
  Clock, Globe, ChevronRight, User, Tag, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CONTACT_STATUSES = ['new', 'contacted', 'qualified', 'closed'];
const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-amber-50 text-amber-700 border-amber-200',
  qualified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
  pending: 'bg-orange-50 text-orange-600 border-orange-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const LeadsTab = ({ headers }) => {
  const [contacts, setContacts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [businessCases, setBusinessCases] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingContact, setDeletingContact] = useState(null);
  const [deletingDemo, setDeletingDemo] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [purging, setPurging] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [cRes, dRes, bcRes, diagRes] = await Promise.all([
        fetch(`${API}/api/admin/contacts`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/demo-requests`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/business-case-leads`, { headers, credentials: 'include' }).catch(() => null),
        fetch(`${API}/api/admin/diagnostic-leads`, { headers, credentials: 'include' }).catch(() => null),
      ]);
      if (cRes.ok) { const d = await cRes.json(); setContacts(d.contacts || []); }
      if (dRes.ok) { const d = await dRes.json(); setDemos(d.demo_requests || []); }
      if (bcRes && bcRes.ok) { const d = await bcRes.json(); setBusinessCases(d.leads || []); }
      if (diagRes && diagRes.ok) { const d = await diagRes.json(); setDiagnostics(d.leads || []); }
    } catch (e) { console.error('Failed to fetch leads data:', e); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateContactStatus = async (submissionId, newStatus) => {
    try {
      await fetch(`${API}/api/admin/contacts/${submissionId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      await fetchData();
    } catch (e) { console.error('Failed to update contact status:', e); }
  };

  const deleteContact = async (contact) => {
    const id = contact.submission_id;
    if (!id) { alert('Cannot delete: no submission ID found'); return; }
    if (!window.confirm(`Delete contact "${contact.name || contact.email}"?`)) return;
    setDeletingContact(id);
    try {
      const res = await fetch(`${API}/api/admin/contacts/${id}`, {
        method: 'DELETE', headers, credentials: 'include',
      });
      if (res.ok) { await fetchData(); }
      else { const err = await res.json(); alert(err.detail || 'Delete failed'); }
    } catch (e) { console.error('Failed to delete contact:', e); }
    finally { setDeletingContact(null); }
  };

  const deleteDemo = async (demo) => {
    const id = demo.request_id;
    if (!id) { alert('Cannot delete: no request ID found'); return; }
    if (!window.confirm(`Delete demo request from "${demo.name || demo.email}"?`)) return;
    setDeletingDemo(id);
    try {
      const res = await fetch(`${API}/api/admin/demo-requests/${id}`, {
        method: 'DELETE', headers, credentials: 'include',
      });
      if (res.ok) { await fetchData(); }
      else { const err = await res.json(); alert(err.detail || 'Delete failed'); }
    } catch (e) { console.error('Failed to delete demo request:', e); }
    finally { setDeletingDemo(null); }
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const handlePurgeTestLeads = async () => {
    const totalTest = contacts.filter(c => isTestLead(c)).length +
      demos.filter(d => isTestLead(d)).length +
      businessCases.filter(b => isTestLead(b)).length +
      diagnostics.filter(d => isTestLead(d)).length;
    if (!window.confirm(`PURGE ${totalTest} test leads across all collections?\n\nThis deletes leads with emails containing: test, TEST, e2e, example.com, company.com\n\nReal leads will NOT be affected.`)) return;
    if (!window.confirm('Are you absolutely sure? This cannot be undone.')) return;
    setPurging(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/bulk/purge-test`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ patterns: ['test', 'TEST', 'e2e', 'example.com', 'company.com'] }),
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Purged ${data.total} test leads.`);
        await fetchData();
      } else { const err = await res.json(); alert(err.detail || 'Purge failed'); }
    } catch (e) { console.error('Failed to purge test leads:', e); }
    finally { setPurging(false); }
  };

  const isTestLead = (item) => {
    const email = (item.email || '').toLowerCase();
    return email.includes('test') || email.includes('e2e') || email.includes('example.com') || email.includes('company.com');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-taxera-blue" />
      </div>
    );
  }

  const DetailRow = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <div>
          <span className="text-2xs text-slate-400 uppercase tracking-wide font-semibold">{label}</span>
          <p className="text-sm text-taxera-charcoal mt-0.5 whitespace-pre-wrap">{value}</p>
        </div>
      </div>
    );
  };

  const totalLeads = contacts.length + demos.length + businessCases.length + diagnostics.length;
  const testLeadCount = contacts.filter(c => isTestLead(c)).length +
    demos.filter(d => isTestLead(d)).length +
    businessCases.filter(b => isTestLead(b)).length +
    diagnostics.filter(d => isTestLead(d)).length;

  return (
    <div className="space-y-8" data-testid="admin-leads">
      {/* Purge bar */}
      {testLeadCount > 0 && (
        <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700 font-medium">
              {testLeadCount} test entries detected across {totalLeads} total leads
            </span>
          </div>
          <button onClick={handlePurgeTestLeads} disabled={purging}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 transition-all disabled:opacity-50"
            data-testid="purge-test-leads-btn">
            {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Purge Test Data
          </button>
        </div>
      )}

      {/* Contact Submissions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-taxera-charcoal flex items-center gap-2">
              <Mail className="w-5 h-5" /> Contact Submissions
            </h2>
            <p className="text-sm text-slate-500 mt-1">{contacts.length} submissions</p>
          </div>
        </div>

        {contacts.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No contact submissions yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((c, i) => {
              const isExpanded = expandedId === (c.submission_id || `c-${i}`);
              return (
                <div
                  key={c.submission_id || i}
                  className="bg-white rounded-xl border border-slate-200 hover:shadow-sm transition-all overflow-hidden"
                  data-testid={`contact-row-${i}`}
                >
                  {/* Collapsed row */}
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer"
                    onClick={() => toggleExpand(c.submission_id || `c-${i}`)}
                  >
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-taxera-charcoal truncate">
                          {c.name || 'Unknown'}
                        </span>
                        {c.company && (
                          <span className="hidden md:inline-flex items-center gap-1 text-2xs text-slate-400">
                            <Building2 className="w-3 h-3" /> {c.company}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{c.email}</span>
                    </div>

                    <div className="hidden md:flex items-center gap-1 text-xs text-slate-400 w-28 shrink-0">
                      <Calendar className="w-3 h-3" />
                      {c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : '—'}
                    </div>

                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={c.status || 'new'}
                        onChange={(e) => updateContactStatus(c.submission_id, e.target.value)}
                        className={`text-xs px-2.5 py-1 rounded-full font-semibold border cursor-pointer focus:outline-none ${
                          STATUS_COLORS[c.status || 'new'] || STATUS_COLORS.new
                        }`}
                        data-testid={`contact-status-${i}`}
                      >
                        {CONTACT_STATUSES.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); deleteContact(c); }}
                      disabled={deletingContact === c.submission_id}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 shrink-0"
                      title="Delete contact"
                      data-testid={`contact-delete-${i}`}
                    >
                      {deletingContact === c.submission_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Expanded detail */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50">
                          <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                            <DetailRow icon={User} label="Name" value={c.name} />
                            <DetailRow icon={Mail} label="Email" value={c.email} />
                            <DetailRow icon={Building2} label="Company" value={c.company} />
                            <DetailRow icon={Phone} label="Phone" value={c.phone} />
                            <DetailRow icon={Globe} label="Source" value={c.source} />
                            <DetailRow icon={Clock} label="Submitted" value={c.submitted_at ? new Date(c.submitted_at).toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' }) : null} />
                          </div>
                          {c.message && (
                            <div className="mt-4 pt-4 border-t border-slate-200">
                              <div className="flex items-center gap-2 mb-2">
                                <MessageSquare className="w-4 h-4 text-slate-400" />
                                <span className="text-2xs text-slate-400 uppercase tracking-wide font-semibold">Message</span>
                              </div>
                              <p className="text-sm text-taxera-charcoal leading-relaxed bg-white rounded-lg p-4 border border-slate-200 whitespace-pre-wrap">
                                {c.message}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Demo Requests */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-taxera-charcoal flex items-center gap-2">
              <FileText className="w-5 h-5" /> Demo Requests
            </h2>
            <p className="text-sm text-slate-500 mt-1">{demos.length} requests</p>
          </div>
        </div>

        {demos.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No demo requests yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {demos.map((d, i) => {
              const isExpanded = expandedId === (d.request_id || `d-${i}`);
              return (
                <div
                  key={d.request_id || i}
                  className="bg-white rounded-xl border border-slate-200 hover:shadow-sm transition-all overflow-hidden"
                  data-testid={`demo-row-${i}`}
                >
                  {/* Collapsed row */}
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer"
                    onClick={() => toggleExpand(d.request_id || `d-${i}`)}
                  >
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-taxera-charcoal truncate">
                          {d.name || 'Unknown'}
                        </span>
                        {d.company && (
                          <span className="hidden md:inline-flex items-center gap-1 text-2xs text-slate-400">
                            <Building2 className="w-3 h-3" /> {d.company}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{d.email}</span>
                    </div>

                    <div className="hidden md:flex items-center gap-1 text-xs text-slate-400 w-28 shrink-0">
                      <Calendar className="w-3 h-3" />
                      {d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : '—'}
                    </div>

                    <div className="shrink-0">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${
                        STATUS_COLORS[d.status || 'pending'] || STATUS_COLORS.pending
                      }`}>
                        {(d.status || 'pending').charAt(0).toUpperCase() + (d.status || 'pending').slice(1)}
                      </span>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); deleteDemo(d); }}
                      disabled={deletingDemo === d.request_id}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 shrink-0"
                      title="Delete demo request"
                      data-testid={`demo-delete-${i}`}
                    >
                      {deletingDemo === d.request_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Expanded detail */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50">
                          <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                            <DetailRow icon={User} label="Name" value={d.name} />
                            <DetailRow icon={Mail} label="Email" value={d.email} />
                            <DetailRow icon={Building2} label="Company" value={d.company} />
                            <DetailRow icon={Phone} label="Phone" value={d.phone} />
                            <DetailRow icon={Clock} label="Preferred Time" value={d.preferred_time} />
                            <DetailRow icon={Calendar} label="Submitted" value={d.submitted_at ? new Date(d.submitted_at).toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' }) : null} />
                          </div>
                          {d.notes && (
                            <div className="mt-4 pt-4 border-t border-slate-200">
                              <div className="flex items-center gap-2 mb-2">
                                <MessageSquare className="w-4 h-4 text-slate-400" />
                                <span className="text-2xs text-slate-400 uppercase tracking-wide font-semibold">Notes</span>
                              </div>
                              <p className="text-sm text-taxera-charcoal leading-relaxed bg-white rounded-lg p-4 border border-slate-200 whitespace-pre-wrap">
                                {d.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Business Case Leads */}
      {businessCases.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-taxera-charcoal flex items-center gap-2">
                <FileText className="w-5 h-5" /> Business Case Leads
              </h2>
              <p className="text-sm text-slate-500 mt-1">{businessCases.length} leads</p>
            </div>
          </div>
          <div className="space-y-2">
            {businessCases.map((bc, i) => {
              const isExpanded = expandedId === (bc.request_id || `bc-${i}`);
              return (
                <div
                  key={bc.request_id || i}
                  className="bg-white rounded-xl border border-slate-200 hover:shadow-sm transition-all overflow-hidden"
                  data-testid={`bc-row-${i}`}
                >
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer"
                    onClick={() => toggleExpand(bc.request_id || `bc-${i}`)}
                  >
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-taxera-charcoal truncate">
                          {bc.full_name || bc.name || 'Unknown'}
                        </span>
                        {bc.company && (
                          <span className="hidden md:inline-flex items-center gap-1 text-2xs text-slate-400">
                            <Building2 className="w-3 h-3" /> {bc.company}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{bc.email}</span>
                    </div>
                    <div className="hidden md:flex items-center gap-2 shrink-0">
                      {bc.products && (
                        <span className="text-2xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          {bc.products.length} products
                        </span>
                      )}
                      {bc.countries && (
                        <span className="text-2xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          {bc.countries} countries
                        </span>
                      )}
                    </div>
                    <div className="hidden md:flex items-center gap-1 text-xs text-slate-400 w-28 shrink-0">
                      <Calendar className="w-3 h-3" />
                      {bc.submitted_at ? new Date(bc.submitted_at).toLocaleDateString() : '—'}
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50">
                          <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                            <DetailRow icon={User} label="Name" value={bc.full_name || bc.name} />
                            <DetailRow icon={Mail} label="Email" value={bc.email} />
                            <DetailRow icon={Building2} label="Company" value={bc.company} />
                            <DetailRow icon={Globe} label="Countries" value={bc.countries ? `${bc.countries} countries` : null} />
                            <DetailRow icon={Tag} label="Products" value={bc.products ? bc.products.join(', ') : null} />
                            <DetailRow icon={Globe} label="Source" value={bc.source} />
                            <DetailRow icon={Calendar} label="Submitted" value={bc.submitted_at ? new Date(bc.submitted_at).toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' }) : null} />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Diagnostic Leads */}
      {diagnostics.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-taxera-charcoal flex items-center gap-2">
                <FileText className="w-5 h-5" /> Diagnostic Leads
              </h2>
              <p className="text-sm text-slate-500 mt-1">{diagnostics.length} leads</p>
            </div>
          </div>
          <div className="space-y-2">
            {diagnostics.map((diag, i) => {
              const isExpanded = expandedId === (diag.request_id || `diag-${i}`);
              return (
                <div
                  key={diag.request_id || i}
                  className="bg-white rounded-xl border border-slate-200 hover:shadow-sm transition-all overflow-hidden"
                  data-testid={`diag-row-${i}`}
                >
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer"
                    onClick={() => toggleExpand(diag.request_id || `diag-${i}`)}
                  >
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-taxera-charcoal truncate">
                          {diag.name || 'Unknown'}
                        </span>
                        {diag.company && (
                          <span className="hidden md:inline-flex items-center gap-1 text-2xs text-slate-400">
                            <Building2 className="w-3 h-3" /> {diag.company}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{diag.email}</span>
                    </div>
                    {diag.health_score !== undefined && (
                      <div className="hidden md:flex items-center gap-1 shrink-0">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                          diag.health_score >= 60 ? 'bg-red-50 text-red-600' :
                          diag.health_score >= 30 ? 'bg-amber-50 text-amber-600' :
                          'bg-emerald-50 text-emerald-600'
                        }`}>
                          Score: {diag.health_score}
                        </span>
                      </div>
                    )}
                    <div className="hidden md:flex items-center gap-1 text-xs text-slate-400 w-28 shrink-0">
                      <Calendar className="w-3 h-3" />
                      {diag.submitted_at ? new Date(diag.submitted_at).toLocaleDateString() : '—'}
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50">
                          <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
                            <DetailRow icon={User} label="Name" value={diag.name} />
                            <DetailRow icon={Mail} label="Email" value={diag.email} />
                            <DetailRow icon={Building2} label="Company" value={diag.company} />
                            <DetailRow icon={Globe} label="Countries" value={diag.country_count ? `${diag.country_count} countries` : null} />
                            <DetailRow icon={Tag} label="Mandates" value={diag.mandate_count ? `${diag.mandate_count} mandates` : null} />
                            <DetailRow icon={Calendar} label="Submitted" value={diag.submitted_at ? new Date(diag.submitted_at).toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' }) : null} />
                          </div>
                          {diag.pain_scores && (
                            <div className="mt-4 pt-4 border-t border-slate-200">
                              <span className="text-2xs text-slate-400 uppercase tracking-wide font-semibold mb-2 block">Pain Scores</span>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                {Object.entries(diag.pain_scores).map(([key, val]) => (
                                  <div key={key} className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                                    <div className="text-lg font-bold text-taxera-charcoal">{val}</div>
                                    <div className="text-2xs text-slate-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
